import React, { FC, useCallback, FocusEvent, useEffect, useState, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { isEqual } from 'lodash-es';
import { CoreFormControlProps } from '../_core/core-form-control';
import { CoreAutocomplete } from '../places-autocomplete/autocomplete.component';
import { UserAvatar } from '../user-avatar';
import { defaultAutoCompleteStyles } from '../inputs/input.styles';
import { UsersAutocompleteOption } from '../../interfaces';
import { Colors } from '../../constants';

export interface UsersAutocompleteProps extends CoreFormControlProps {
	value?: UsersAutocompleteOption;
	options: UsersAutocompleteOption[];
	placeholder?: string;
	hasMore?: boolean;
	loading?: boolean;
	startAdornment?: ReactNode;
	disablePortal?: boolean;
	clearOnBlur?: boolean;
	onSelect?(user: UsersAutocompleteOption | undefined): void;
	onInputChange?(search: string): void;
	fetchMore?(count: number): void;
	onBlur?(e: FocusEvent): void;
}

export const UsersAutocomplete: FC<UsersAutocompleteProps> = (
	{
		value,
		options,
		placeholder,
		onSelect,
		onInputChange,
		startAdornment,
		disablePortal,
		...autocompleteProps
	}
) => {
	const [inputValue, setInputValue] = useState('');

	useEffect(() => {
		if (value) {
			setInputValue(value.fullName || '');
		}
	}, [value]);

	const onSuggestionSelected = useCallback(
		(user: UsersAutocompleteOption): void => {
			onSelect?.(user);
		},
		[onSelect]
	);

	return (
		<CoreAutocomplete
			{...autocompleteProps}
			value={value}
			options={options}
			clearIcon={null}
			disablePortal={disablePortal}
			sx={defaultAutoCompleteStyles()}
			onChange={(e, suggestion) => {
				onSuggestionSelected(suggestion as UsersAutocompleteOption);
			}}
			filterOptions={options => options}
			inputValue={inputValue}
			isOptionEqualToValue={(option, value) => isEqual(option, value)}
			onInputChange={(e, value): void => {
				setInputValue(value || '');
				onInputChange?.(value?.trim());
			}}
			getOptionKey={option => (option as UsersAutocompleteOption)?.id}
			getOptionLabel={option => (option as UsersAutocompleteOption)?.fullName ?? ''}
			textFieldProps={{
				InputProps: {
					placeholder,
					startAdornment: value ? (
						<UserAvatar
							fullName={value.fullName}
							avatarSrc={value.avatarSrc}
							emptyAvatarVariant={value.emptyAvatarVariant}
							sx={{ mr: '8px', width: '28px', height: '28px' }}
						/>
					) : (
						startAdornment
					),
				},
			}}
			componentsProps={{
				paper: {
					sx: {
						'& .MuiAutocomplete-listbox': {
							maxHeight: '235px',
						},
					},
				},
			}}
			renderOption={(props, option) => (
				<li {...props} key={props.key ?? option.id}>
					<UserAvatar
						fullName={option.fullName}
						avatarSrc={option.avatarSrc}
						emptyAvatarVariant={option.emptyAvatarVariant}
						sx={{ mr: '8px', width: '28px', height: '28px' }}
					/>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '4px',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Montserrat, sans-serif',
								fontSize: '16px',
								fontWeight: '700',
								lineHeight: 'normal',
								color: Colors.white,
							}}
						>
							{option.fullName}
						</Typography>

						{option.userName && (
							<Typography
								sx={{
									fontFamily: 'Montserrat, sans-serif',
									fontSize: '14px',
									color: Colors.white_a80,
									fontWeight: '500',
									lineHeight: '1.2em',
								}}
							>
								@{option.userName}
							</Typography>
						)}
					</Box>
				</li>
			)}
		/>
	);
};
